import React, { useState } from "react";
import { bindActionCreators } from "redux";
import { connect } from "react-redux";
import {
  Dialog,
  Typography,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  TextField,
  DialogTitle,
  CircularProgress,
  Switch,
  Select,
  Input,
  MenuItem,
} from "@material-ui/core";
import { Formik } from "formik";

import { DropNCrop } from "../../../utils/DropNCrop";

import * as schoolDUCK from "../../../../redux/ducks/school.duck";
import { setLoader } from "../../../../redux/ducks/load.duck";
import {
  createPlaylist,
  updatePlaylist,
} from "../../../crud/api/playlist.crud";
import notice from "../../../utils/notice";

const PlaylistDialog = ({ mode, setDialog, playlist, onRefresh, contents }) => {
  const title = mode === "editing" ? "Edit Playlist" : "Create New Playlist";
  const open = mode === "editing" || mode === "create";
  const [loading, setLoading] = useState(false);

  const create = async (values) => {
    try {
      setLoading(true);
      const dataToSend = {
        ...values,
        playlist_contents_attributes: values.playlist_contents_attributes.map(
          (id) => ({
            content_id: id,
          })
        ),
      };
      await createPlaylist(dataToSend);
      notice("Playlist created successfully", "success");
      setDialog("close");
      if (onRefresh) {
        onRefresh();
      }
    } catch (error) {
      notice("Error creating playlist", "error");
    } finally {
      setLoading(false);
    }
  };

  const update = async (values) => {
    try {
      setLoading(true);
      const dataToUpdate = {
        ...values,
        id: playlist.id,
        playlist_contents_attributes: values.playlist_contents_attributes.map(
          (id) => ({
            content_id: id,
          })
        ),
      };
      await updatePlaylist(dataToUpdate);
      notice("Playlist updated successfully", "success");
      setDialog("close");
      if (onRefresh) {
        onRefresh();
      }
    } catch (error) {
      notice("Error updating playlist", "error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={() => setDialog("close")}
      maxWidth="md"
      fullWidth
      id="school_dialog"
    >
      <Formik
        initialValues={{
          title: playlist?.title || "",
          description: playlist?.description || "",
          cover_url: playlist?.cover_url || "",
          content_type: playlist?.content_type || "jpg",
          active: playlist?.active || false,
          start: playlist?.start || false,
          playlist_contents_attributes:
            playlist?.playlist_contents_attributes?.map((item) => item.id) ||
            [],
        }}
        validate={(values) => {
          const errors = {};

          if (!values.title || values.title.trim() === "") {
            errors.title = "Title is required";
          }

          if (!values.description || values.description.trim() === "") {
            errors.description = "Description is required";
          }

          if (!values.cover_url) {
            errors.cover_url = "Cover image is required";
          }

          if (
            !values.playlist_contents_attributes ||
            values.playlist_contents_attributes.length === 0
          ) {
            errors.playlist_contents_attributes =
              "At least one content must be selected";
          }

          return errors;
        }}
        onSubmit={async (values, { setSubmitting }) => {
          try {
            if (playlist?.id) {
              await update(values);
            } else {
              await create(values);
            }
          } catch (error) {
            // Error handling is done in create/update functions
          } finally {
            setSubmitting(false);
          }
        }}
      >
        {({
          values,
          errors,
          touched,
          handleChange,
          handleBlur,
          handleSubmit,
          setFieldValue,
          isSubmitting,
        }) => (
          <form noValidate={true} autoComplete="off" onSubmit={handleSubmit}>
            <DialogTitle disableTypography>
              <Typography variant="h6">{title}</Typography>
            </DialogTitle>
            <DialogContent>
              <Grid container spacing={3}>
                <Grid container item xs={12}>
                  <TextField
                    required
                    fullWidth
                    variant="outlined"
                    id="playlist_title_input_dialog"
                    label="Title"
                    name="title"
                    value={values.title}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    helperText={touched.title && errors.title}
                    error={Boolean(touched.title && errors.title)}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    required
                    fullWidth
                    variant="outlined"
                    id="playlist_description_input_dialog"
                    label="Description"
                    name="description"
                    value={values.description}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    helperText={touched.description && errors.description}
                    error={Boolean(touched.description && errors.description)}
                  />
                </Grid>
                <Grid container item xs={12}>
                  <h5>Cover image *</h5>
                  <div style={{ width: "100%" }}>
                    <DropNCrop
                      setterFunction={setFieldValue}
                      file={values.cover_url}
                      field="cover_url"
                    />
                  </div>
                  {touched.cover_url && errors.cover_url && (
                    <div
                      style={{
                        color: "#f44336",
                        fontSize: "0.75rem",
                        marginTop: "4px",
                      }}
                    >
                      {errors.cover_url}
                    </div>
                  )}
                </Grid>
                <Grid container item xs={12} style={{ overflow: "auto" }}>
                  <h5>Playlist content *</h5>
                  <Select
                    multiple
                    required
                    fullWidth
                    id="playlist_contents_attributes"
                    label="Playlist content"
                    name="playlist_contents_attributes"
                    value={values.playlist_contents_attributes}
                    input={<Input variant="" />}
                    renderValue={(selected) => {
                      // Combinar ambas fuentes de datos para mostrar todos los contenidos seleccionados
                      const allContents = [
                        ...(playlist?.playlist_contents || []),
                        ...(contents.items || []),
                      ];

                      // Eliminar duplicados basándose en el ID
                      const uniqueContents = allContents.reduce(
                        (acc, current) => {
                          const existingItem = acc.find(
                            (item) => item.id === current.id
                          );
                          if (!existingItem) {
                            acc.push(current);
                          }
                          return acc;
                        },
                        []
                      );

                      return uniqueContents
                        .filter((item) => selected.includes(item.id))
                        .map((item) => item.title)
                        .join(", ");
                    }}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    helperText={
                      touched.playlist_contents_attributes &&
                      errors.playlist_contents_attributes
                    }
                    error={Boolean(
                      touched.playlist_contents_attributes &&
                        errors.playlist_contents_attributes
                    )}
                  >
                    {contents.items.map((fa) => (
                      <MenuItem key={fa.id} value={fa.id}>
                        {fa.title}
                      </MenuItem>
                    ))}
                  </Select>
                </Grid>
                <Grid item xs={12}>
                  <div style={{ marginBottom: "16px" }}>
                    <Switch
                      name="active"
                      id="active_input_dialog"
                      checked={values.active}
                      onBlur={handleBlur}
                      onChange={(e) => {
                        handleChange(e);
                        if (!e.target.checked) {
                          setFieldValue("start", false);
                        }
                      }}
                      label={"Active"}
                    />
                    <span>Active</span>
                  </div>
                  <div>
                    <Switch
                      name="start"
                      id="start_input_dialog"
                      checked={values.start}
                      onBlur={handleBlur}
                      onChange={handleChange}
                      disabled={!values.active}
                      label={"Start"}
                    />
                    <span>Feature on homepage</span>
                  </div>
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions>
              <Button
                id="cancel_school_btn_dialog"
                onClick={() => setDialog("close")}
                color="secondary"
              >
                Cancel
              </Button>
              <button
                type="submit"
                id="submit_playlist_btn_dialog"
                disabled={
                  isSubmitting ||
                  !values.title ||
                  !values.description ||
                  !values.cover_url ||
                  !values.playlist_contents_attributes ||
                  values.playlist_contents_attributes.length === 0
                }
                className="btn btn-primary btn-elevate kt-login__btn-primary d-flex"
              >
                {mode === "editing" ? "Submit" : "Create"}
                {loading && (
                  <CircularProgress
                    color="inherit"
                    size={18}
                    style={{ marginLeft: 10 }}
                  />
                )}
              </button>
            </DialogActions>
          </form>
        )}
      </Formik>
    </Dialog>
  );
};

const mapStateToProps = (store) => ({});

const mapDispatchToProps = (dispatch) => ({
  schoolActions: bindActionCreators(schoolDUCK.actions, dispatch),
  settingLoader: bindActionCreators(setLoader, dispatch),
});

export default connect(mapStateToProps, mapDispatchToProps)(PlaylistDialog);
